import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminEmailListService } from "../service";
import { handleError } from "../../../shared/functions/handleError";
import {
  IEmailList,
  IEmailStats,
} from "../../../shared/interfaces/email-list/IEmailList";
import { useEmailListPreviewColumns } from "../hooks/useEmailListPreviewColumns";
import CustomDataGrid from "../../../shared/components/CustomDataGrid";
import { Box, Button, Card } from "@mui/material";
import StatisticCard from "../../../shared/components/StatisticCard";
import { getEmailListStatusIcon } from "../../../shared/functions/getEmailListStatusIcon";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import InvoiceStatisticsSkeleton from "../../invoice/components/InvoiceStatisticsSkeleton";
import ConfirmModal from "../../../shared/components/ConfirmModal";
import { enqueueSnackbar } from "notistack";
import EmailListAddRecipientModal from "./EmailListAddRecipientModal";

const previewKeys = ["name", "language"];
const EmailListPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isConfirmationOpened, setIsConfirmationOpened] = useState(false);
  const [isAddRecipientModalOpened, setIsAddRecipientModalOpened] =
    useState(false);
  const [data, setDate] = useState<IEmailList | undefined>(undefined);
  const columns = useEmailListPreviewColumns();
  const { t } = useTranslation();
  const pendingRecipients = data?.recipients?.filter(
    (recipient) => recipient.status === "PENDING",
  );
  const handleCloseConfirmationModal = () => {
    setIsConfirmationOpened(false);
  };

  const handleSendMails = () => {
    AdminEmailListService.send(Number(id))
      .then((response) => {
        if (response) {
          enqueueSnackbar(
            t("toasts.successfullySent", {
              number: response?.queued,
            }),
            {
              variant: "success",
            },
          );
          navigate(-1);
          handleCloseConfirmationModal();
        }
      })
      .catch(handleError);
  };

  useEffect(() => {
    if (id) {
      AdminEmailListService.getSingle(id).then(setDate).catch(handleError);
    }
  }, [id]);
  return (
    <Card sx={{ marginTop: "30px" }}>
      {data === undefined ? (
        <InvoiceStatisticsSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ mb: "20px" }}>
          {previewKeys?.map((statistic) => {
            return (
              <StatisticCard
                key={statistic}
                size={{
                  xs: 12,
                  lg: 6,
                }}
                getTitle={(item) => t(`emails.${item}`)}
                getIcon={(item) =>
                  getEmailListStatusIcon(item as keyof IEmailStats)
                }
                title={statistic}
                getValue={(item) => String(data[item as keyof typeof data])}
              />
            );
          })}
        </Grid>
      )}

      <CustomDataGrid
        columns={columns}
        rows={data?.recipients || []}
        loading={data === undefined}
        hideFooterPagination
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant={"contained"}
          color={"secondary"}
          sx={{ marginTop: "20px" }}
          type={"button"}
          onClick={() => navigate(-1)}
        >
          {t("button.cancel")}
        </Button>

        <Button
          variant={"contained"}
          color={"success"}
          sx={{ margin: "20px 0 0 10px" }}
          onClick={() => setIsAddRecipientModalOpened(true)}
        >
          {t("invoice.addItems")}
        </Button>

        {pendingRecipients?.length ? (
          <Button
            variant={"contained"}
            sx={{ margin: "20px 0 0 10px" }}
            onClick={() => setIsConfirmationOpened(true)}
          >
            {t("button.sendMails")}
          </Button>
        ) : (
          ""
        )}
      </Box>
      <ConfirmModal
        confirmText={t("emails.confirmationText")}
        open={isConfirmationOpened}
        handleSubmit={handleSendMails}
        handleClose={handleCloseConfirmationModal}
      />
      <EmailListAddRecipientModal
        open={isAddRecipientModalOpened}
        setDate={setDate}
        handleClose={() => setIsAddRecipientModalOpened(false)}
      />
    </Card>
  );
};

export default EmailListPreview;
